import { union } from 'lodash-es';
import { NutrientTarget, NutrientTargets, Nutrients, INFOODS } from './data-model/food';

const maximumGuard = 100000;
// this normalizes so it has both limits, and also makes a copy so it is safe to modify it
function normalizedTarget(target: NutrientTarget): NutrientTarget {
  if (!target) {
    return { min: 0, max: maximumGuard };
  } else {
    return {
      min: target.min || 0,
      max: target.max || maximumGuard,
    };
  }
}
/** Calculate more tight suggested targets for each meal, daily targets and what's already scheduled in other meals. */
export function actualMealTargets(
  mealTargets: NutrientTargets,
  dayNutrients: Nutrients,
  dayTargets: NutrientTargets,
  otherMealTargets: NutrientTargets[],
): NutrientTargets {
  // this is array of otherMeals _including_ this one!
  let allMealTargets = otherMealTargets.concat([mealTargets]);

  let newTargets: NutrientTargets = {};

  if (mealTargets === undefined) {
    return newTargets;
  }
  // "size" of this meal in terms of calories relative to the entire day, is used to scale other nutrients
  let mealCalories = mealTargets[INFOODS.Calories] && mealTargets[INFOODS.Calories].target;
  let allMealsCaloriesSum = allMealTargets && allMealTargets
    .map(t => (t[INFOODS.Calories] != undefined && t[INFOODS.Calories].target != undefined ? t[INFOODS.Calories].target : null))
    .filter(target => target != null)
    .reduce((acc, target) => acc + target, 0);
  let relativeSize = mealCalories && allMealsCaloriesSum && allMealsCaloriesSum > 0 ? mealCalories / allMealsCaloriesSum : 1 / allMealTargets.length;

  // for each possible tags in day and meal targets, to process them all
  let mergedTags = union(Object.keys(dayTargets), Object.keys(mealTargets));
  for (let tag of mergedTags) {
    let dayTarget = normalizedTarget(dayTargets[tag]);
    let dayValue = dayNutrients[tag] || 0;
    let mealTarget = normalizedTarget(mealTargets[tag]);
    newTargets[tag] = mealTarget;

    // NOTE THAT THIS IS calculated on UNNORMALIZED targets - so we won't get huge sum of normalized maximums!
    let allMinTotal = allMealTargets
      .map(t => (t[tag] != undefined && t[tag].min != undefined ? t[tag].min : null))
      .filter(min => min != null)
      .reduce((acc, min) => acc + min, 0);
    let allMaxTotal = allMealTargets
      .map(t => (t[tag] != undefined && t[tag].max != undefined ? t[tag].max : null))
      .filter(max => max != null)
      .reduce((acc, max) => acc + max, 0);
    if (allMaxTotal == 0) {
      allMaxTotal = maximumGuard; // this happens if no maximums are defined
    }

    // if accepting all meal _minimums_ would overrun daily _maximum_
    // (this is probably because some earlier meal ran over its limit)
    if (dayValue + allMinTotal > dayTarget.max) {
      // such per-meal targets are completely abandoned and replaced by calculation from the daily  maximum average
      // (but we can only recalculate this one now)
      mealTarget.min = (dayTarget.max - dayValue) * relativeSize;

      // otherwise, meal minimums are below daily maximum, but are they below daily minimum?
    } else if (dayValue + allMinTotal < dayTarget.min) {
      // if yes, boost them all up by the same amount
      mealTarget.min += (dayTarget.min - dayValue - allMinTotal) * relativeSize;
    }

    // if accepting all meal _maximums_ would underrun daily _minimum_
    if (dayValue + allMaxTotal < dayTarget.min) {
      // such per-meal targets are completely abandoned and replaced by calculation from the daily minimum average
      // (but we can only recalculate this one now)
      mealTarget.max = (dayTarget.min - dayValue) * relativeSize;

      // otherwise, we have valid meal maximums and they are above daily maximum, but are they above daily maximum?
    } else if (allMaxTotal < maximumGuard && dayValue + allMaxTotal > dayTarget.max) {
      // if yes, nerf them all down by the same amount
      mealTarget.max -= (dayValue + allMaxTotal - dayTarget.max) * relativeSize;
      // if there are no per-meal maximums, but there is daily maximum
    } else if (allMaxTotal === maximumGuard && dayTarget.max < maximumGuard) {
      mealTarget.max = (dayTarget.max - dayValue) * relativeSize;
    }

    // if min and max collide, max always has precedence
    if (mealTarget.min > mealTarget.max) {
      mealTarget.min = mealTarget.max;
    }
    // if it is supposed to have a goal value, make sure it is still in-between the min/max
    if (mealTargets[tag] && mealTargets[tag].target != undefined) {
      if (mealTarget.min > 0 && mealTarget.max < maximumGuard) {
        mealTarget.target = (mealTarget.min + mealTarget.max) / 2;
      } else if (mealTarget.min > 0) {
        mealTarget.target = Math.max(mealTarget.min, mealTargets[tag].target);
      } else if (mealTarget.max > 0 && mealTarget.max < maximumGuard) {
        mealTarget.target = Math.min(mealTarget.max, mealTargets[tag].target);
      }
    }

    // round all the numbers
    mealTarget.min = mealTarget.min && Math.round(mealTarget.min);
    mealTarget.max = mealTarget.max && Math.round(mealTarget.max);
    mealTarget.target = mealTarget.target && Math.round(mealTarget.target);

    // if one side is unbounded, remove it
    if (mealTarget.min == undefined || mealTarget.min <= 0) {
      delete mealTarget.min;
    }
    if (mealTarget.max == undefined || mealTarget.max <= 0 || mealTarget.max >= maximumGuard) {
      delete mealTarget.max;
    }
    if (mealTarget.target == undefined || mealTarget.target < 0) {
      delete mealTarget.target;
    }
    // if no data left, remove the entire limit
    if (Object.keys(mealTarget).length <= 0) {
      delete newTargets[tag];
    }
  }

  return newTargets;
}
